import { Option } from '@sapphire/result';

import {
  completesToClick,
  ComputeFeasibilityParams,
  FeasibilityQuotaSet,
  SurveyMetadataForm,
} from '@ps/survey-utils';

import {
  FeasibilityPriceOfferDto,
  FeasibilityRequestDto,
} from '../dtos/feasibility.dto';

import { FeasibilityDefaultQuotaCriteriaPayloadMapper } from './feasibility-default-quota-criteria.payload.mapper';
import { FeasibilityQuotaPayloadMapper } from './feasibility-quota.payload.mapper';

export interface FeasibilityPayloadResult {
  readonly payload: FeasibilityRequestDto;
  readonly quotaSet: FeasibilityQuotaSet;
}

export class FeasibilityPayloadMapper {
  public build(data: ComputeFeasibilityParams): FeasibilityPayloadResult {
    const { surveyId, user, metadata, qualifications, quotas } = data;
    const completesRequired = Option.from(metadata.completes).unwrapOr(0);
    const untaggedDefaultQuotaCriteria =
      new FeasibilityDefaultQuotaCriteriaPayloadMapper(qualifications).map();

    const {
      quotas: quotaPayloads,
      quotaSet,
      defaultQuotaCriteria,
    } = new FeasibilityQuotaPayloadMapper(
      quotas,
      untaggedDefaultQuotaCriteria,
      completesRequired,
    ).map();

    const payload: FeasibilityRequestDto = {
      buyer_id: user.companyId,
      user_id: user.id,
      project_id: surveyId,
      completes_required: completesRequired,
      expected_ir: Option.from(metadata.incidence).unwrapOr(0),
      expected_loi: Option.from(metadata.loi).unwrapOr(0),
      field_time: Option.from(metadata.fieldTime).unwrapOr(0),
      survey_localization: this._localization(metadata),
      quotas: quotaPayloads,
      defaultQuota: [
        {
          criteria: defaultQuotaCriteria,
        },
      ],
      offer_price: this._offerPrice(data.offerPrice),
      is_exclusion: data.isExclusion,
      survey_grouping: [...data.surveyGroupIds],
      grouping_statuses: [...data.groupingStatuses],
      click_balancing: Number(metadata.clickBalancing),
      estimated_clicks: this._estimatedClicks(metadata),
      is_optimized_payload: true,
      // Sent only when a supplier is actually selected — an empty list would
      // read as "these suppliers", not "no supplier constraint".
      ...(data.supplierIds.length
        ? { supplier_ids: [...data.supplierIds] }
        : {}),
    };

    return { payload, quotaSet };
  }

  /** A zero override is no override — it prices as inactive, same as null. */
  private _offerPrice(offerPrice: Option<number>): FeasibilityPriceOfferDto {
    return offerPrice
      .filter((price) => price !== 0)
      .mapOr({ value: null, active: false }, (price) => ({
        value: price,
        active: true,
      }));
  }

  /** Only meaningful with click balancing on — that is what spends against clicks. */
  private _estimatedClicks(metadata: SurveyMetadataForm): number {
    const completes = Option.from(metadata.completes).unwrapOr(0);
    const incidence = Option.from(metadata.incidence).unwrapOr(0);

    return metadata.clickBalancing && incidence
      ? completesToClick(completes, incidence)
      : 0;
  }

  /** Throws on a survey with no language or country — neither is optional by then. */
  private _localization(metadata: SurveyMetadataForm): string {
    return Option.all([
      Option.from(metadata.selectedLanguages[0]),
      Option.from(metadata.selectedCountries[0]),
    ])
      .map(([languageShortCode, countryCode]) =>
        [languageShortCode, countryCode].join('_'),
      )
      .unwrap();
  }
}
